import * as Yup from 'yup'
import {useState, useEffect, FC, useContext} from 'react'
import {ID} from '@/_metronic/helpers'
import {ProductAvailability} from '../../../core/_models'
import {createAvailability, updateAvailability} from '../../../core/_requests'
import {Supplier} from '../../../../suppliers/core/_models'
import {Formik, Field, Form} from 'formik'
import {CustomSelect} from '@/components/form/CustomSelect'
import {InputText} from '@/components/form/InputText'
import {Checkbox} from '@/components/form/CheckBox'
import {FormActions} from '@/components/form/Actions'
import {ProductContext} from '../../ProductContext'
import {Lookup} from './Lookup'

type Props = {
  availability?: ProductAvailability
  close: React.MouseEventHandler
}

const schema = Yup.object().shape({
  supplier: Yup.string().required('Supplier is required'),
  name: Yup.string().min(3, 'Minimum 3 characters').required('Name is required'),
  sku: Yup.string().min(3, 'Minimum 3 characters').required('SKU is required'),
  pricing: Yup.object().shape({
    discount: Yup
      .number()
      .required('Discount rate is required')
      .typeError('Must be a number'),
    amount: Yup
      .number()
      .min(0.1, 'Minimum 0')
      .required('Amount is required'),
    currency: Yup.string().min(3, '3 characters').max(3).required('Currency is required')
  })
})

const AvailabilityForm : FC<Props>= ({availability, close}) => {
  const [apiAvailable, setApiAvailable] = useState<boolean|undefined>()
  const {
    suppliers,
    fetchAvailability,
    availability: existing,
    setAction,
    setItemForUpdate,
    product
  } = useContext(ProductContext)

  const getApiAvailable = (supplierId: ID) => {
    const found = suppliers
      .find((s: Supplier) => s._id === supplierId)

    return found?.apiAvailable
  }
  const hasApi = getApiAvailable(availability?.supplier)

  useEffect(() => {
    setApiAvailable(hasApi)
  }, [hasApi])

  if (product === undefined) {
    return (<>No product</>)
  }

  return (
    <Formik
      initialValues={availability || {
        subjectToVat: false
      } as ProductAvailability}
      validationSchema={schema}
      onSubmit={async (values, {setSubmitting}) => {
        try {
          if (!availability) {
            await createAvailability(product._id, values)
          } else {
            await updateAvailability(product._id, values)
          }
          setAction(undefined)
          setItemForUpdate(undefined)
          fetchAvailability(product._id)
        } catch {
          console.log('Error')
        }
      }}
    >
      {({values, setFieldValue}) => (
        <Form id="product-availability" className='form'>
          <Field
            component={CustomSelect}
            default='Select a supplier'
            options={suppliers.map((s: Supplier) => {
              return {
                value: s._id,
                label: s.name,
                disabled: existing?.find((e: ProductAvailability) => {
                  return s._id === e.supplier
                }) !== undefined
              }
            })}
            label="Supplier"
            name="supplier"
            id="supplier"
            disabled={availability !== undefined}
            required={true}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setApiAvailable(getApiAvailable(e.target.value as ID))
              setFieldValue('pricing.currency', suppliers.find((s: Supplier) => s._id === e.target.value as ID)?.currency)
            }}
          />
          {apiAvailable === true && availability === undefined && (
            <Lookup
              supplierId={values.supplier}
              product={product}
            />
          )}

          <div className={'row'}>
            <div className={'col-8'}>
              <Field
                name="name"
                type="text"
                component={InputText}
                label="Product name"
                description="Supplier product name"
                placeholder="100"
                required={true}
                value={values?.name}
                disabled={apiAvailable}
              />
            </div>
            <div className={'col-4'}>
              <Field
                name="sku"
                type="text"
                component={InputText}
                label="Product SKU"
                description="Supplier product SKU"
                placeholder="100"
                required={true}
                value={values?.sku}
                disabled={apiAvailable}
              />
            </div>
          </div>

          <div className={'row'}>
            <div className={'col-4'}>
              <Field
                id='pricing.discount'
                name="pricing.discount"
                component={InputText}
                type="number"
                label="Discount"
                placeholder="0"
                description="Has discount in %"
                required={true}
                value={values?.pricing?.discount}
                disabled={apiAvailable}
              />
            </div>
            <div className={'col-4'}>
              <Field
                id='pricing.amount'
                name="pricing.amount"
                component={InputText}
                label="Amount"
                type="number"
                placeholder="0"
                description="Gross amount"
                required={true}
                value={values?.pricing?.amount}
                disabled={apiAvailable}
              />
            </div>
            <div className={'col-4'}>
              <Field
                id='pricing.currency'
                name="pricing.currency"
                className="text-uppercase"
                component={InputText}
                label="Currency"
                placeholder="EUR"
                required={true}
                value={values?.pricing?.currency}
                disabled={apiAvailable}
              />
            </div>
          </div>

          <Field
            id='subjectToVat'
            name="subjectToVat"
            component={Checkbox}
            label="Subject to VAT"
            defaultChecked={availability?.subjectToVat}
          />
          <Field
            component={FormActions}
            close={close}
            disabled={availability !== undefined && apiAvailable}
          />
        </Form>
      )}
    </Formik>
  )
}

export {AvailabilityForm}
